<script setup lang="ts">
import { reactive, watch } from 'vue'
import type { QuoteContent, WebsiteLeadCaptureState, WebsiteLeadSubmission } from '../../types'

const props = defineProps<{
  content: QuoteContent
  leadCapture: WebsiteLeadCaptureState
  compact?: boolean
}>()
const emit = defineEmits<{ submit: [submission: WebsiteLeadSubmission] }>()

const form = reactive({
  name: '',
  email: '',
  phone: '',
  service: '',
  location: '',
  preferredContact: 'Email',
  details: '',
  website: '',
})

function submit(): void {
  if (props.leadCapture.submitting) return

  emit('submit', {
    name: form.name,
    email: form.email,
    phone: form.phone,
    message: [
      `Service: ${form.service}`,
      `Location: ${form.location}`,
      `Preferred contact: ${form.preferredContact}`,
      '',
      'Project details:',
      form.details,
    ].join('\n'),
    website: form.website,
  })
}

watch(
  () => props.leadCapture.succeeded,
  (succeeded) => {
    if (!succeeded) return

    form.name = ''
    form.email = ''
    form.phone = ''
    form.service = ''
    form.location = ''
    form.preferredContact = 'Email'
    form.details = ''
    form.website = ''
  },
)
</script>

<template>
  <form class="site:relative site:grid site:gap-5" novalidate @submit.prevent="submit">
    <div class="site:absolute site:left-[-10000px] site:top-auto site:h-px site:w-px site:overflow-hidden" aria-hidden="true">
      <label>Website<input v-model="form.website" name="website" tabindex="-1" autocomplete="off" /></label>
    </div>

    <div class="site:grid site:gap-5 site:@min-[40rem]:grid-cols-2">
      <label class="site:grid site:gap-2 site:text-sm site:font-semibold">
        Name
        <input v-model="form.name" name="name" autocomplete="name" required class="site:min-h-12 site:w-full site:border site:border-(--site-color-border) site:bg-(--site-color-background) site:px-4 site:text-base site:font-normal site:text-(--site-color-text) site:outline-none site:transition site:focus:border-(--site-color-primary) site:focus:ring-3 site:focus:ring-(--site-color-primary)/15" :style="{ borderRadius: 'var(--site-radius-sm)' }" />
        <span v-if="leadCapture.errors?.name" class="site:text-xs site:font-medium site:text-red-600">{{ leadCapture.errors.name }}</span>
      </label>

      <label class="site:grid site:gap-2 site:text-sm site:font-semibold">
        Email
        <input v-model="form.email" name="email" type="email" autocomplete="email" required class="site:min-h-12 site:w-full site:border site:border-(--site-color-border) site:bg-(--site-color-background) site:px-4 site:text-base site:font-normal site:text-(--site-color-text) site:outline-none site:transition site:focus:border-(--site-color-primary) site:focus:ring-3 site:focus:ring-(--site-color-primary)/15" :style="{ borderRadius: 'var(--site-radius-sm)' }" />
        <span v-if="leadCapture.errors?.email" class="site:text-xs site:font-medium site:text-red-600">{{ leadCapture.errors.email }}</span>
      </label>
    </div>

    <div class="site:grid site:gap-5" :class="compact ? 'site:@min-[40rem]:grid-cols-3' : 'site:@min-[40rem]:grid-cols-2'">
      <label class="site:grid site:gap-2 site:text-sm site:font-semibold">
        <span>Phone <span class="site:font-normal site:text-(--site-color-muted-text)">(optional)</span></span>
        <input v-model="form.phone" name="phone" type="tel" autocomplete="tel" class="site:min-h-12 site:w-full site:border site:border-(--site-color-border) site:bg-(--site-color-background) site:px-4 site:text-base site:font-normal site:text-(--site-color-text) site:outline-none site:transition site:focus:border-(--site-color-primary) site:focus:ring-3 site:focus:ring-(--site-color-primary)/15" :style="{ borderRadius: 'var(--site-radius-sm)' }" />
        <span v-if="leadCapture.errors?.phone" class="site:text-xs site:font-medium site:text-red-600">{{ leadCapture.errors.phone }}</span>
      </label>

      <label class="site:grid site:gap-2 site:text-sm site:font-semibold">
        Service needed
        <select v-model="form.service" name="service" required class="site:h-12 site:w-full site:border site:border-(--site-color-border) site:bg-(--site-color-background) site:px-4 site:pr-10 site:text-base site:font-normal site:text-(--site-color-text) site:outline-none site:transition site:focus:border-(--site-color-primary) site:focus:ring-3 site:focus:ring-(--site-color-primary)/15" :style="{ borderRadius: 'var(--site-radius-sm)' }">
          <option value="" disabled>Select a service</option>
          <option v-for="service in content.services" :key="service" :value="service">{{ service }}</option>
        </select>
      </label>

      <label class="site:grid site:gap-2 site:text-sm site:font-semibold" :class="compact ? undefined : 'site:@min-[40rem]:col-span-2'">
        Postal code or service location
        <input v-model="form.location" name="location" autocomplete="postal-code" required class="site:min-h-12 site:w-full site:border site:border-(--site-color-border) site:bg-(--site-color-background) site:px-4 site:text-base site:font-normal site:text-(--site-color-text) site:outline-none site:transition site:focus:border-(--site-color-primary) site:focus:ring-3 site:focus:ring-(--site-color-primary)/15" :style="{ borderRadius: 'var(--site-radius-sm)' }" />
      </label>
    </div>

    <label class="site:grid site:gap-2 site:text-sm site:font-semibold">
      Project details
      <textarea v-model="form.details" name="message" rows="4" minlength="10" required class="site:w-full site:resize-y site:border site:border-(--site-color-border) site:bg-(--site-color-background) site:px-4 site:py-3 site:text-base site:font-normal site:leading-7 site:text-(--site-color-text) site:outline-none site:transition site:focus:border-(--site-color-primary) site:focus:ring-3 site:focus:ring-(--site-color-primary)/15" :style="{ borderRadius: 'var(--site-radius-sm)' }" />
      <span v-if="leadCapture.errors?.message" class="site:text-xs site:font-medium site:text-red-600">{{ leadCapture.errors.message }}</span>
    </label>

    <fieldset class="site:grid site:gap-2">
      <legend class="site:text-sm site:font-semibold">Preferred contact method</legend>
      <div class="site:flex site:flex-wrap site:gap-2">
        <label v-for="method in ['Email', 'Phone', 'Text message']" :key="method" class="site:cursor-pointer">
          <input v-model="form.preferredContact" type="radio" name="preferred-contact" :value="method" class="site:sr-only" />
          <span class="site:inline-flex site:min-h-10 site:items-center site:border site:px-4 site:text-sm site:font-semibold site:transition" :class="form.preferredContact === method ? 'site:border-(--site-color-primary) site:bg-(--site-color-primary)/10 site:text-(--site-color-primary)' : 'site:border-(--site-color-border) site:bg-(--site-color-background) site:text-(--site-color-muted-text)'" :style="{ borderRadius: 'var(--site-radius-sm)' }">{{ method }}</span>
        </label>
      </div>
    </fieldset>

    <div class="site:flex site:flex-wrap site:items-center site:gap-4">
      <button type="submit" class="site-site-button" :disabled="leadCapture.submitting">{{ leadCapture.submitting ? 'Sending...' : content.submitLabel }}</button>
      <p v-if="leadCapture.message" class="site:text-sm site:font-medium" :class="leadCapture.succeeded ? 'site:text-(--site-color-primary)' : 'site:text-red-600'" role="status" aria-live="polite">{{ leadCapture.message }}</p>
    </div>
  </form>
</template>
